import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Slider,
  Typography,
  Avatar,
  CircularProgress
} from '@mui/material';
import {
  PhotoCamera,
  Rotate90DegreesCcw,
  ZoomIn,
  Save,
  Close
} from '@mui/icons-material';
import api from '../../utils/axios';
import AvatarEditor from 'react-avatar-editor';

const ProfileImageUploader = ({ onImageUpdate, currentImage }) => {
  const [open, setOpen] = useState(false);
  const [image, setImage] = useState(null);
  const [scale, setScale] = useState(1.2);
  const [rotate, setRotate] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const editorRef = useRef(null);
  
  const baseUrl = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5000';
  
  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('File too large. Maximum size is 5MB.');
        return;
      }

      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please upload an image file.');
        return;
      }
      
      setImage(file);
      setOpen(true);
      setError('');
    }
  };
  
  const handleScale = (event, newValue) => {
    setScale(newValue);
  };
  
  const handleRotate = () => {
    setRotate((prevRotate) => (prevRotate + 90) % 360);
  };
  
  const handleSave = async () => {
    if (editorRef.current) {
      setLoading(true);
      setError('');
      
      try {
        // Get the cropped canvas from the editor
        const canvas = editorRef.current.getImageScaledToCanvas();
        
        // Convert the canvas to a blob
        canvas.toBlob(async (blob) => {
          try {
            // Create a file from the blob
            const file = new File([blob], "profile-image.png", { type: "image/png" });
            
            // Create a FormData instance
            const formData = new FormData();
            formData.append('profileImage', file);
            
            // Send the request
            const response = await api.post('/users/profile/image', formData, {
              headers: {
                'Content-Type': 'multipart/form-data'
              }
            });
            
            console.log('Upload successful:', response.data);
            
            // Update parent component with a slight delay to ensure state updates properly
            if (onImageUpdate) {
              // First update the parent component
              onImageUpdate(response.data.profileImage);
            }
            
            // Add a small delay before closing the dialog to prevent rendering issues
            setTimeout(() => {
              try {
                // Close the dialog after a short delay
                setOpen(false);
              } catch (err) {
                console.error('Error closing dialog:', err);
              }
            }, 500);
            
          } catch (error) {
            console.error('Error uploading image:', error);
            setError('Failed to upload image. Please try again.');
          } finally {
            setLoading(false);
          }
        }, 'image/png');
      } catch (err) {
        console.error('Error processing image:', err);
        setError('Failed to process image. Please try again.');
        setLoading(false);
      }
    }
  };
  
  const handleClose = () => {
    setOpen(false);
    setImage(null);
    setScale(1.2);
    setRotate(0);
    setError('');
  };
  
  return (
    <Box>
      {/* Avatar with upload button */}
      <Box sx={{ position: 'relative', mb: 2 }}>
        <Avatar
          src={currentImage ? `${baseUrl}${currentImage}` : ''}
          alt="Profile"
          sx={{ width: 120, height: 120 }}
        />
        <label htmlFor="profile-image-input">
          <input
            accept="image/*"
            id="profile-image-input"
            type="file"
            style={{ display: 'none' }}
            onChange={handleImageChange}
          />
          <IconButton
            color="primary"
            aria-label="upload profile picture"
            component="span"
            sx={{
              position: 'absolute',
              bottom: 0,
              right: 0,
              backgroundColor: 'white'
            }}
          >
            <PhotoCamera />
          </IconButton>
        </label>
      </Box>
      
      {/* Help text */}
      <Typography variant="body2" color="textSecondary" align="center" sx={{ mt: 1 }}>
        Click the camera icon to upload a profile photo
      </Typography>
      
      {/* Error message */}
      {error && (
        <Typography color="error" variant="body2" align="center" sx={{ mt: 1 }}>
          {error}
        </Typography>
      )}
      
      {/* Edit Dialog */}
      <Dialog 
        open={open} 
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Edit Profile Photo
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 2 }}>
            {/* Avatar Editor */}
            {image && (
              <AvatarEditor
                ref={editorRef}
                image={image}
                width={250}
                height={250}
                border={50}
                color={[0, 0, 0, 0.6]}
                scale={scale}
                rotate={rotate}
                borderRadius={125}
              />
            )}
            
            {/* Controls */}
            <Box sx={{ width: '100%', mt: 2 }}>
              <Typography gutterBottom>Zoom</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ZoomIn sx={{ mr: 1 }} />
                <Slider
                  value={scale}
                  min={1}
                  max={3}
                  step={0.01}
                  onChange={handleScale}
                  aria-labelledby="zoom-slider"
                />
              </Box>
              
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                <Button
                  variant="outlined"
                  startIcon={<Rotate90DegreesCcw />}
                  onClick={handleRotate}
                >
                  Rotate
                </Button>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleClose} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            variant="contained" 
            color="primary"
            startIcon={loading ? <CircularProgress size={20} /> : <Save />}
            disabled={loading}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProfileImageUploader; 